import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Copy, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const WelcomeModal = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user === undefined) return;
    const seen = localStorage.getItem("m16_welcomed");
    const isReturning = localStorage.getItem("m16_returning_user") === "1";
    // Show only to brand-new visitors (never logged in, never dismissed welcome)
    if (!seen && !isReturning && user === null) {
      const tm = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(tm);
    }
  }, [user]);

  const dismiss = (goLogin) => {
    localStorage.setItem("m16_welcomed", "1");
    setOpen(false);
    if (goLogin) navigate("/login");
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText("WELCOME10");
      setCopied(true);
      toast.success(t("welcomeOfferCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard not available */ }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
      data-testid="welcome-modal"
    >
      <div className="relative w-full max-w-md bg-[#0A0A0B] border border-[#D4AF37]/30 rounded-3xl p-8 page-enter shadow-[0_30px_80px_-20px_rgba(212,175,55,0.25)]">
        <button
          onClick={() => dismiss(false)}
          data-testid="welcome-close-btn"
          className="absolute top-4 end-4 p-1.5 text-[#9CA3AF] hover:text-[#D4AF37] transition-colors"
          aria-label="close"
        >
          <X size={18} />
        </button>

        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] bg-[#D4AF37]/5">
            <Sparkles size={28} />
          </div>
        </div>

        <div className="text-center mb-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80 mb-2">M16 / ام16</div>
          <h2 className="font-display text-3xl font-bold gold-gradient-text">{t("welcomeTitle")}</h2>
        </div>

        <p className="text-sm text-[#9CA3AF] text-center leading-relaxed mb-6">{t("welcomeMsg")}</p>

        {/* Coupon banner */}
        <div className="bg-[#D4AF37]/8 border border-[#D4AF37]/40 rounded-2xl p-5 mb-7" data-testid="welcome-coupon-banner">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">{t("welcomeOfferEyebrow")}</div>
            <div className="font-display text-lg mb-3">{t("welcomeOfferTitle")}</div>
            <button
              onClick={copyCode}
              data-testid="welcome-copy-code-btn"
              className="w-full inline-flex items-center justify-center gap-3 bg-[#0A0A0B] border-2 border-dashed border-[#D4AF37] rounded-xl py-3 px-4 hover:border-solid hover:bg-[#D4AF37]/5 transition-all group"
            >
              <span className="text-xs text-[#9CA3AF]">{t("welcomeOfferCode")}</span>
              <span className="font-display text-xl font-bold text-[#D4AF37] num-ltr tracking-widest">WELCOME10</span>
              {copied
                ? <Check size={16} className="text-[#10B981]" />
                : <Copy size={14} className="text-[#9CA3AF] group-hover:text-[#D4AF37]" />}
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => dismiss(true)}
            data-testid="welcome-signup-btn"
            className="btn-gold w-full py-3 rounded-full"
          >
            {t("signUpOrLogin")}
          </button>
          <button
            onClick={() => dismiss(false)}
            data-testid="welcome-later-btn"
            className="btn-outline-gold w-full py-3 rounded-full"
          >
            {t("continueLater")}
          </button>
        </div>
      </div>
    </div>
  );
};
